import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import CassetteLoader from '../components/CassetteLoader';
import { SketchPageShell, SketchBox, SketchButton } from '../components/sketchy';
import { logInfo, logError } from '../services/logtail';
import ga from '../services/analytics';
import { preserveUtmParams, removeParamsPreservingUtm } from '../utils/utm';
import { consumeGDriveReturnPath } from '../services/gdriveService';

/**
 * GDriveCallback - Landing page after Google Drive OAuth redirect
 * 
 * The actual URL param processing happens in AuthContext.tsx (parent).
 * This component just waits for the state to be set and redirects to dashboard.
 * 
 * Flow:
 * 1. Backend redirects here with ?gdrive_success=true&gdrive_email=...
 * 2. AuthContext.tsx (parent) reads params and sets driveLinked=true
 * 3. This component sees driveLinked=true and redirects to dashboard
 */
const GDriveCallback: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { driveLinked, driveEmail, user, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);


  // Track page view when component mounts
  useEffect(() => {
    ga.pageView('/auth/gdrive/callback', 'Google Drive Callback - Cassette');
  }, []);

  // Check for error in URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gdriveError = params.get('storage_error') ?? params.get('gdrive_error');
    const failedProvider = params.get('provider') ?? 'gdrive';
    if (gdriveError) {
      const decodedError = decodeURIComponent(gdriveError);
      logError('GDriveCallback: Error from OAuth', { error: decodedError, provider: failedProvider });
      setError(decodedError);
      const newUrl = removeParamsPreservingUtm(['gdrive_error', 'storage_error', 'provider']);
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Redirect based on auth state
  useEffect(() => {
    // Don't redirect if there's an error
    if (error) {
      return;
    }

    // Wait for auth to finish loading
    if (loading) {
      logInfo('GDriveCallback: Waiting for auth to load...');
      return;
    }

    // If not logged in, go to login
    if (!user) {
      logInfo('GDriveCallback: No user, redirecting to login');
      navigate(preserveUtmParams('/auth/login'), { replace: true });
      return;
    }

    // If drive is linked (set by AuthContext from URL params), go to dashboard
    if (driveLinked) {
      const returnPath = consumeGDriveReturnPath('/dashboard');
      logInfo('GDriveCallback: Drive linked, redirecting after OAuth', { email: driveEmail, returnPath });
      navigate(preserveUtmParams(returnPath), { replace: true });
      return;
    }

    // If drive not linked after a short delay, something went wrong - go back to link page
    const timeout = setTimeout(() => {
      logInfo('GDriveCallback: Drive still not linked after timeout, redirecting to storage page');
      navigate(preserveUtmParams('/auth/storage?reason=failed&provider=gdrive'), { replace: true });
    }, 2000);

    return () => clearTimeout(timeout);
  }, [loading, user, driveLinked, driveEmail, navigate, error]);

  // Show error UI if there was an OAuth error
  if (error) {
    return (
      <SketchPageShell className="min-h-screen flex flex-col items-center justify-center px-4">
        <SketchBox className="p-6 max-w-md w-full text-center">
          <div className="text-xl font-bold mb-4 text-red-500">
            ❌ {t('gdrive.callback.connectionFailed')}
          </div>
          <div className="text-sm text-[var(--color-muted)] mb-6">
            {error}
          </div>
          <SketchButton
            variant="primary"
            onClick={() => navigate(preserveUtmParams('/auth/storage?reason=failed&provider=gdrive'), { replace: true })}
            className="w-full px-6 py-3"
          >
            {t('gdrive.callback.tryAgain')}
          </SketchButton>
        </SketchBox>
      </SketchPageShell>
    );
  }

  // Just show loader - redirect happens automatically, no need to show success message that flashes
  return (
    <SketchPageShell className="min-h-screen flex flex-col items-center justify-center px-4">
      <CassetteLoader size={80} variant="overlay" text={t('gdrive.callback.connecting')} />
    </SketchPageShell>
  );
};

export default GDriveCallback;
